/**
 * PulsingFAB — Floating Action Button
 * ────────────────────────────────────
 * Fires a custom DOM event `focusflow:open-task-modal` so any page can
 * listen without prop-drilling.  Hidden automatically in immersive mode.
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const PulsingFAB: React.FC = () => {
  const location = useLocation();
  const [immersive, setImmersive] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ active?: boolean }>;
      setImmersive(Boolean(ce.detail?.active));
    };
    window.addEventListener('focusflow:immersive-mode', handler as EventListener);
    return () => window.removeEventListener('focusflow:immersive-mode', handler as EventListener);
  }, []);

  // Hide on timer immersive mode or on public routes
  const hide = (immersive && location.pathname === '/timer') ||
    ['/','auth','/admin/login'].includes(location.pathname);

  if (hide) return null;

  const fireOpen = () => {
    window.dispatchEvent(new CustomEvent('focusflow:open-task-modal'));
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '2rem',
        right: '2rem',
        zIndex: 500,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      {/* Tooltip */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            key="fab-tooltip"
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            style={{
              background: 'rgba(9,14,28,0.88)',
              backdropFilter: 'blur(14px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              padding: '6px 14px',
              fontSize: '12px',
              fontWeight: 700,
              color: '#E2E8F0',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 18px rgba(0,0,0,0.4)',
              pointerEvents: 'none',
            }}
          >
            New Task
          </motion.div>
        )}
      </AnimatePresence>

      {/* Button */}
      <div style={{ position: 'relative' }}>
        {/* Outer pulse ring */}
        <span className="fab-ring" aria-hidden="true" />

        <motion.button
          aria-label="Create new task"
          onClick={fireOpen}
          onHoverStart={() => setHovered(true)}
          onHoverEnd={() => setHovered(false)}
          whileHover={{ scale: 1.10 }}
          whileTap={{ scale: 0.93 }}
          style={{
            position: 'relative',
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #7C3AED 0%, #00E5FF 100%)',
            boxShadow: '0 4px 24px rgba(124,58,237,0.55), 0 0 0 2px rgba(124,58,237,0.2)',
            zIndex: 1,
          }}
        >
          <motion.span
            animate={{ rotate: hovered ? 45 : 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 22 }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Plus size={22} color="#fff" strokeWidth={2.5} />
          </motion.span>
        </motion.button>
      </div>
    </div>
  );
};

export default PulsingFAB;
